import { useEffect, useMemo, useRef } from 'react';
import { generatePath, NavLink } from 'react-router-dom';
import ButtonPseudo from '../button/button-pseudo';
import ButtonSecondary from '../button/button-secondary';
import SidePanel from '../side-panel/side-panel';
import { SidePanelType } from '../side-panel/types';
import IconPower from '../icons/power';
import formatDate from '../../helpers/format-date';
import useQueryState from '../table/hooks/use-query-state';
import {
  columns,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/sessions';
import useGetList from '../../hooks/use-get-list';
import { DeviceSession, DeviceSidepanelTypes } from './types';
import useFilters from '../table/hooks/use-filters';
import { PermissionEnum } from '../../base-types';
import IconArrowRight from '../icons/arrow-right';
import useScroll from '../../hooks/use-scroll';
import { SchemaFilterType } from '../table/types';
import Table from '../table/table';

import cn from './styles.module.css';
import SidePanelLoader from '../side-panel/loader';

const formatDevicesSessionsList = (
  list: DeviceSession[],
  auditLink: string,
  onLinkClick?: () => void,
  activePermissions?: PermissionEnum[],
  customerDevices?: boolean,
  itemId?: string,
  backLink?: string,
): DeviceSession[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let auditLinkValue;

    if (result.auditId) {
      if (customerDevices) {
        auditLinkValue = generatePath(auditLink, {
          customerId: itemId as string,
          logId: result.auditId,
        });
      } else {
        auditLinkValue = generatePath(auditLink, {
          logId: result.auditId,
        });
      }
    }

    const render: Record<string, React.JSX.Element> = {};
    render.createdAt = (
      <div>{result.createdAt ? formatDate(result.createdAt, true) : '-'}</div>
    );
    render.updatedAt = (
      <div>{result.updatedAt ? formatDate(result.updatedAt, true) : '-'}</div>
    );
    render.deletedAt = (
      <div>{result.deletedAt ? formatDate(result.deletedAt, true) : '-'}</div>
    );
    render.accessTokenExpiresAt = (
      <div>
        {result.accessTokenExpiresAt
          ? formatDate(result.accessTokenExpiresAt, true)
          : '-'}
      </div>
    );
    render.refreshTokenExpiresAt = (
      <div className={cn.deviceSessionRefreshExpires}>
        <div>
          {result.refreshTokenExpiresAt
            ? formatDate(result.refreshTokenExpiresAt, true)
            : '-'}
        </div>
        {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
          result.auditId && (
            <NavLink
              to={auditLinkValue || ''}
              className={cn.auditLogLink}
              onClick={() => (onLinkClick ? onLinkClick() : undefined)}
              target={onLinkClick ? '_self' : '_blank'}
              state={
                backLink
                  ? {
                      backLink,
                    }
                  : undefined
              }
            >
              View Logs
              <IconArrowRight width={12} height={12} />
            </NavLink>
          )}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

export default function DeviceSessionSidepanel({
  panelIsOpen,
  handleClose,
  deleteSessionIsAvailable,
  isLoading,
  onEndSession,
  device,
  getDeviceSessions,
  itemId,
  deviceId,
  onLinkClick,
  deviceSessions,
  activePermissions,
  isFetching,
  deviceSessionsIsLoading,
  deviceSessionsPagination,
  auditLink,
  customerDevices,
  sessionIsAvailable,
  backLink,
}: DeviceSidepanelTypes) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const { getListAction, setPage, onFilter, errorList } =
    useGetList<DeviceSession>({
      getList: getDeviceSessions,
      onFilterChange,
      onSortChange,
      schema,
      itemId,
      entityId: deviceId,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatDevicesSessionsList(
      deviceSessions as DeviceSession[],
      auditLink,
      onLinkClick,
      activePermissions,
      customerDevices,
      itemId,
      backLink,
    );

    return data;
  }, [
    deviceSessions,
    filters,
    activePermissions,
    onLinkClick,
    auditLink,
    customerDevices,
    itemId,
    backLink,
  ]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (panelIsOpen && filters && !deviceSessions && sessionIsAvailable) {
      const nextfilters = { ...filters };

      if (filters.cursor) {
        delete nextfilters.cursor;
      }

      getListAction({
        filters: {
          ...filters,
          take: {
            value: ['20'],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [filters, deviceSessions, sessionIsAvailable, panelIsOpen]);

  let content;

  if (!device) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <div className={cn.deviceInfoWrapper}>
        <div className={cn.deviceInfoTop}>
          <div className={`${cn.deviceInfoTopContent} ${cn.black}`}>
            {`Device created: ${formatDate(
              device?.updatedAt || device?.createdAt,
              true,
            )}`}
          </div>
          <div className={cn.deviceInfoTopContent}>
            {`${device?.platform}, ${device?.browser}`}
          </div>
          <div className={cn.deviceInfoTopContent}>{`IP: ${device?.ip}`}</div>
        </div>
        {sessionIsAvailable && (
          <div className={cn.deviceInfoSessions} ref={scrollRef}>
            <Table
              columns={columns}
              data={formattedData || []}
              onSort={() => {}}
              filters={filters}
              sort={{}}
              columnsLabels={columnsLabels}
              tableClassName={!deviceSessions?.length ? cn.devicesEmpty : ''}
              onFilterChange={() => {}}
              filtersComponent={filtersComponent}
              handleSubmit={handleSubmit}
              isLoading={deviceSessionsIsLoading || isFetching}
              take="20"
              error={errorList}
              setPage={(data) => {
                setPage({
                  ...data,
                  filters: {
                    ...filters,
                  },
                });
              }}
              pagination={deviceSessionsPagination}
              withVerticalScroll
              withScroll
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <SidePanel
      closeOnClickOutside
      isOpen={panelIsOpen}
      onClose={handleClose}
      title="Device sessions"
      type={SidePanelType.AML}
      sidepanelExpandedKey="device-session-sidepanel"
      footer={
        <div className={cn.deviceInfoFooter}>
          {deleteSessionIsAvailable && (
            <ButtonSecondary
              className={cn.endDeviceSessionButton}
              text={isLoading ? 'Loading...' : 'End all sessions'}
              onClick={onEndSession}
              icon={<IconPower width={24} height={24} />}
              bold
            />
          )}

          <ButtonPseudo text="Close" onClick={handleClose} bold />
        </div>
      }
    >
      {content}
    </SidePanel>
  );
}
